'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface NavigationItem {
  label: string;
  href: string;
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const primaryNavItems: NavigationItem[] = [
    { label: 'Home', href: '/homepage' },
    { label: 'The Movement', href: '/the-movement' },
    { label: 'Festival Guide', href: '/festival-guide' },
    { label: 'Global Impact', href: '/global-impact' },
    { label: 'Join Us', href: '/join-the-movement' },
  ];

  const secondaryNavItems: NavigationItem[] = [
    { label: 'Resources Hub', href: '/resources-hub' },
    { label: 'Community Stories', href: '/community-stories' },
    { label: 'Community Forum', href: '/community-forum' },
    { label: 'News & Updates', href: '/news-updates' },
    { label: 'Partner Network', href: '/partner-network' },
    { label: 'Humanitarian Action', href: '/humanitarian-action' },
    { label: 'Donate', href: '/donate' },
    { label: 'Global Calendar', href: '/global-calendar' },
    { label: 'Research & Evidence', href: '/research-evidence' },
    { label: 'Media Center', href: '/media-center' },
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Journal History', href: '/journal-history' },
    { label: 'Admin', href: '/admin' },
  ];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isBioModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isBioModalOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    if (isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreMenuOpen]);

  const isActiveLink = (href: string) => {
    return pathname === href;
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMoreMenuOpen(false);
  };

  const handleMoreMenuToggle = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMoreMenuOpen(false);
  };

  return (
    <header
      suppressHydrationWarning
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-card shadow-md' : 'bg-card'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-20 px-6 lg:px-8">
          {/* Logo */}
          <Link
            href="/homepage"
            className="flex items-center space-x-3 group transition-transform duration-300 hover:scale-105"
            onClick={closeMobileMenu}
          >
            <div className="relative">
              <AppImage
                src="/assets/images/LOGO-SIGLINE_PNG-1769633021018.png"
                alt="SIG Day Logo - The Global No Enemy Festival"
                width={48}
                height={48}
                className="transition-transform duration-300 group-hover:scale-110 rounded-md"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-headline font-bold text-primary leading-tight">
                SIG Day
              </span>
              <span className="text-xs font-body text-muted-foreground leading-tight">
                The Global No Enemy Festival
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" suppressHydrationWarning>
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-all duration-300 ${
                  isActiveLink(item.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* More Menu */}
            <div className="relative" suppressHydrationWarning ref={moreMenuRef}>
              <button
                onClick={handleMoreMenuToggle}
                className={`flex items-center space-x-1 px-4 py-2 rounded-md font-body text-sm font-medium transition-all duration-300 ${
                  isMoreMenuOpen
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                }`}
                aria-expanded={isMoreMenuOpen}
                aria-haspopup="true"
              >
                <span>More</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`transition-transform duration-300 ${
                    isMoreMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isMounted && isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-xl py-2 z-50 animate-fade-in">
                  {secondaryNavItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className={`block px-4 py-2 font-body text-sm transition-colors duration-200 ${
                        isActiveLink(item.href)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted hover:text-primary'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              href="/festival-guide"
              className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md pulse-gentle"
            >
              Begin Your Journey
            </Link>
            
            {/* Shija's Profile Photo - Clickable for Biography */}
            <button
              onClick={() => setIsBioModalOpen(true)}
              className="relative group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full"
              aria-label="View Shija Mayala's biography"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary shadow-md transition-all duration-300 hover:scale-110 hover:shadow-lg hover:border-accent animate-blink cursor-pointer">
                <AppImage
                  src="/assets/images/Shija_PassChina-1769631519261.jpg"
                  alt="Shija Mayala - Founder of SIG Day"
                  width={48}
                  height={48}
                  className="object-cover w-full h-full"
                />
              </div>
              {/* Tooltip on hover */}
              <div className="absolute right-0 top-14 w-48 bg-card border border-border rounded-lg shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <p className="text-xs font-body text-foreground font-semibold">Shija Mayala</p>
                <p className="text-xs font-body text-muted-foreground mt-1">Founder & Visionary</p>
                <p className="text-xs font-body text-accent mt-1">Click to read biography</p>
              </div>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={handleMobileMenuToggle}
            className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-colors duration-200"
            aria-label="Toggle mobile menu"
            suppressHydrationWarning
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMounted && isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-20 bg-card z-40 overflow-y-auto">
          <nav className="flex flex-col p-6 space-y-1">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-md font-body text-base font-medium transition-colors duration-200 ${
                  isActiveLink(item.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div className="pt-4 pb-2">
              <div className="h-px bg-border" />
            </div>

            {secondaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                className={`px-4 py-3 rounded-md font-body text-base font-medium transition-colors duration-200 ${
                  isActiveLink(item.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div className="pt-6">
              <Link
                href="/festival-guide"
                onClick={closeMobileMenu}
                className="block w-full px-6 py-3 bg-accent text-accent-foreground text-center rounded-md font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-md"
              >
                Begin Your Journey
              </Link>
            </div>
          </nav>
        </div>
      )}

      {/* Biography Modal */}
      {isMounted && isBioModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-card rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-headline font-bold text-primary">Founder Biography</h2>
              <button
                onClick={() => setIsBioModalOpen(false)}
                className="p-2 rounded-md hover:bg-muted transition-colors duration-200"
                aria-label="Close biography"
              >
                <Icon name="XMarkIcon" size={24} className="text-foreground" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Profile Section */}
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-primary shadow-lg">
                    <AppImage
                      src="/assets/images/Shija_PassChina-1769631519261.jpg"
                      alt="Shija Mayala - Founder of SIG Day"
                      width={128}
                      height={128}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-headline font-bold text-foreground mb-2">Shija Mayala</h3>
                  <p className="text-sm font-body text-muted-foreground mb-3">Founder & Visionary Leader</p>
                  <p className="text-sm font-body text-foreground leading-relaxed">
                    A peace-builder, researcher, and social entrepreneur dedicated to transforming global consciousness through forgiveness and reconciliation.
                  </p>
                </div>
              </div>

              {/* Biography Content */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">Early Life & Foundation</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    Born on July 17, 1983, in Igabulilo Village, Magu District, Simiyu Region, Tanzania, Shija Mayala grew up witnessing cycles of violence and division that would later shape his life's mission. His early experiences in Tanzania instilled in him a deep understanding of the human cost of conflict and the transformative power of compassion.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">Professional Background</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    Shija built a distinguished career as an ICT and cybersecurity professional in Tanzania, with a strong foundation in Computer Science and ICT security. His career was defined by analytical thinking, structured problem-solving, and a commitment to sustainable solutions. However, deep reflection on global humanitarian challenges and social polarization led him to a profound realization about the roots of conflict.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">The Transformation</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    Through his journey from technology and governance into global peace advocacy, Shija became convinced that traditional approaches to peace—focused solely on political agreements and institutional reforms—were missing the most critical element: the human heart. He observed that the conflicts we see in the world are reflections of the conflicts we carry within ourselves.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">Founding SIG Day</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    On April 17, 2024, Shija officially founded SIG Day (The Global No Enemy Festival), redefining peace as an emotional, humanitarian, and moral practice centered on forgiveness, reconciliation, and compassionate action. The concept of 'no enemy' emerged from his conviction that sustainable peace could only take root when individuals made the courageous choice to release hatred and embrace forgiveness—not as a sign of weakness, but as an act of profound strength.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">Global Impact & Vision</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    As an international speaker on peace-building, forgiveness, and humanitarian action, Shija champions a world where individuals and institutions consciously choose to live with 'no enemy.' His work integrates inner healing with tangible humanitarian impact, proving that if we could create a global moment—one day where millions of people simultaneously choose compassion over conflict—we could catalyze a transformation that extends far beyond April 17th.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">Philosophy & Approach</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    "Peace is not the absence of conflict, but the presence of compassion. When we choose to see no enemy, we unlock humanity's greatest potential for transformation and collective healing." This philosophy represents Shija's life's work: combining analytical rigor from his technology background with deep humanitarian compassion to create evidence-based frameworks for sustainable peace.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-headline font-semibold text-primary mb-2">Looking Forward</h4>
                  <p className="text-sm font-body text-foreground leading-relaxed text-justify">
                    Today, Shija continues to lead the SIG Day movement with unwavering dedication, working to expand its global reach and deepen its impact. His vision includes establishing peace education programs, creating regional peace centers, and building a worldwide network of individuals committed to the practice of forgiveness and reconciliation.
                  </p>
                </div>
              </div>

              {/* Call to Action */}
              <div className="mt-8 p-4 bg-muted rounded-lg border border-border">
                <p className="text-sm font-body text-foreground text-center">
                  Join Shija and thousands of others in building a world without enemies.
                </p>
                <div className="mt-4 flex justify-center">
                  <Link
                    href="/join-the-movement"
                    onClick={() => setIsBioModalOpen(false)}
                    className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md"
                  >
                    Join The Movement
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;